/**
 * Device-local draft for a job application held until email verification completes.
 *
 * Used when Variant B blocks submit: save cover letter here, show the apply verify modal,
 * then auto-submit via CREATE_JOB_APPLICATION after verification completes.
 *
 * sessionStorage only — does not survive device switches. Cross-device users land back on
 * the job page and submit again (cover letter may still be available if "Save cover letter" ran).
 *
 * Scoped to SSO account id (same pattern as pendingEmailVerification) so an account switch
 * in the same tab cannot auto-submit another user's draft.
 */
import { currentAccountId, sessionStorageOrNull as storage } from '@/apps/verifyEmail/helpers/sessionAccountStorage';

export type PendingJobApplication = {
  accountId: string;
  jobId: string;
  applicationText: string;
  saveApplication: boolean;
};

export type PendingJobApplicationInput = {
  jobId: string;
  applicationText: string;
  saveApplication: boolean;
  accountId?: string;
};

export const PENDING_JOB_APPLICATION_KEY = 'sc_pending_job_application';

function isValidDraft(value: unknown): value is PendingJobApplication {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const draft = value as PendingJobApplication;
  return (
    typeof draft.accountId === 'string' &&
    draft.accountId.length > 0 &&
    typeof draft.jobId === 'string' &&
    draft.jobId.length > 0 &&
    typeof draft.applicationText === 'string' &&
    typeof draft.saveApplication === 'boolean'
  );
}

/** @returns true when the draft was written. */
export function savePendingJobApplication(draft: PendingJobApplicationInput): boolean {
  const session = storage();
  const id = draft.accountId || currentAccountId();
  if (!session || !id) {
    return false;
  }

  const payload: PendingJobApplication = {
    accountId: id,
    jobId: draft.jobId,
    applicationText: draft.applicationText,
    saveApplication: draft.saveApplication,
  };

  if (!isValidDraft(payload)) {
    return false;
  }

  try {
    session.setItem(PENDING_JOB_APPLICATION_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * Draft for the currently authenticated account only.
 * Clears stale drafts left by a previous user in this tab.
 */
export function getPendingJobApplication(): PendingJobApplication | null {
  const session = storage();
  if (!session) {
    return null;
  }

  const raw = session.getItem(PENDING_JOB_APPLICATION_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!isValidDraft(parsed)) {
      clearPendingJobApplication();
      return null;
    }

    const id = currentAccountId();
    if (!id || parsed.accountId !== id) {
      clearPendingJobApplication();
      return null;
    }

    return parsed;
  } catch {
    clearPendingJobApplication();
    return null;
  }
}

export function clearPendingJobApplication(): void {
  const session = storage();
  if (!session) {
    return;
  }

  session.removeItem(PENDING_JOB_APPLICATION_KEY);
}
